/*!
 * gainmyedge.com — accessibility remediation for the Pandectes GDPR consent UI
 * Fixes A11Y-001 .. A11Y-013 from the cookie-dialog audit (28 Aug 2026).
 *
 * Why this shape: the CMP renders inside <pandectes-cmp>'s OPEN shadow root and
 * re-renders the dialog on every state change (notice -> preferences -> widget,
 * and again on language change). Page-level CSS cannot cross a shadow boundary and
 * a one-shot patch is wiped by the next render, so everything is (a) injected INTO
 * the shadow root and (b) re-applied from a MutationObserver.
 *
 * Load after jQuery, defer, on every page.
 */
(function ($) {
  'use strict';

  var HOST_TAG = 'pandectes-cmp';
  var STATUS_ID = 'a11y-consent-status';
  var STYLE_ID = 'a11y-consent-style';

  var lastTrigger = null;   // control to restore focus to when the modal closes
  var inerted = [];         // background elements we made inert
  var trapHandler = null;   // keydown handler while the modal is open
  var noticeAnnounced = false;

  /* ---------------------------------------------------------------- helpers */

  function deepActive() {
    var a = document.activeElement;
    while (a && a.shadowRoot && a.shadowRoot.activeElement) a = a.shadowRoot.activeElement;
    return a;
  }

  function focusables(root) {
    var sel = 'a[href],button:not([disabled]),input:not([type="hidden"]):not([disabled]),' +
              'select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    return Array.prototype.filter.call(root.querySelectorAll(sel), function (el) {
      var r = el.getBoundingClientRect();
      return (r.width || r.height) && getComputedStyle(el).visibility !== 'hidden';
    });
  }

  /* ------------------------------------------------- shadow-root stylesheet */
  /* A11Y-011 target size · A11Y-012 widget focus ring · A11Y-013 forced colours */

  var SHADOW_CSS = [
    /* A11Y-012: the reopen widget was falling back to Chrome's 1px default ring */
    'button.widget:focus-visible{outline:2px solid #FCF8F2 !important;outline-offset:3px !important;}',
    /* A11Y-011: grow the language select's hit area without changing its text size */
    '.dialog select{min-width:44px;min-height:44px;padding:0 4px;text-align:center;cursor:pointer;}',
    /* A11Y-008: the scroll container becomes focusable, so it needs an indicator */
    '.dialog .body[tabindex="0"]:focus-visible{outline:2px solid #FCF8F2;outline-offset:-3px;}',
    '.a11y-sr-only{position:absolute !important;width:1px;height:1px;margin:-1px;padding:0;',
    'overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0;}',
    /* A11Y-013: keep switch state visible when forced-colors overrides background-color */
    '@media (forced-colors: active){',
    '  [role="switch"]{forced-color-adjust:none;background:ButtonFace !important;',
    '    border:2px solid ButtonBorder !important;}',
    '  [role="switch"][aria-checked="true"]{background:Highlight !important;}',
    '  [role="switch"] > *{background:ButtonText !important;}',
    '  .dialog{border:1px solid CanvasText !important;}',
    '}'
  ].join('\n');

  function injectCss(sr) {
    if (sr.getElementById(STYLE_ID)) return;
    var st = document.createElement('style');
    st.id = STYLE_ID;
    st.textContent = SHADOW_CSS;
    sr.appendChild(st);
  }

  /* ------------------------------------------- A11Y-006 status announcements */

  function statusNode(sr) {
    var n = sr.getElementById(STATUS_ID);
    if (!n) {
      n = document.createElement('div');
      n.id = STATUS_ID;
      n.className = 'a11y-sr-only';
      n.setAttribute('role', 'status');
      n.setAttribute('aria-live', 'polite');
      sr.appendChild(n);
    }
    return n;
  }

  function announce(sr, msg) {
    var n = statusNode(sr);
    n.textContent = '';
    window.setTimeout(function () { n.textContent = msg; }, 60);
  }

  /* ----------------------------------------------------- per-render patches */

  function patchDialog(sr, dlg) {
    var prefs = dlg.classList.contains('preferences');

    /* A11Y-009 — name the dialog from its own heading instead of duplicating it */
    var h = dlg.querySelector('h2, .title');
    if (h) {
      if (!h.id) h.id = 'a11y-consent-title';
      dlg.setAttribute('aria-labelledby', h.id);
      dlg.removeAttribute('aria-label');
    }

    /* A11Y-004 — the language <select> had no accessible name at all */
    Array.prototype.forEach.call(dlg.querySelectorAll('select'), function (sel) {
      if (!sel.getAttribute('aria-label')) {
        sel.setAttribute('aria-label', 'Consent banner language');
      }
    });

    /* A11Y-010 — target="_blank" with no warning */
    Array.prototype.forEach.call(dlg.querySelectorAll('a[target="_blank"]'), function (a) {
      a.setAttribute('rel', 'noopener noreferrer');
      if (!a.querySelector('.a11y-newtab')) {
        var s = document.createElement('span');
        s.className = 'a11y-sr-only a11y-newtab';
        s.textContent = ' (opens in a new tab)';
        a.appendChild(s);
      }
    });

    if (!prefs) return;

    /* A11Y-007 — link each category description to its switch */
    Array.prototype.forEach.call(dlg.querySelectorAll('[role="switch"]'), function (sw, i) {
      var section = sw.closest('.category, .item, li, section') || sw.parentElement;
      if (!section) return;
      var desc = section.querySelector('p, .description, .text');
      if (!desc) {
        // fall back to the paragraph that follows the switch's row
        var row = sw.parentElement;
        desc = row && row.nextElementSibling;
      }
      if (!desc || desc.contains(sw)) return;
      if (!desc.id) desc.id = 'a11y-consent-desc-' + i;
      sw.setAttribute('aria-describedby', desc.id);
    });

    /* A11Y-008 — make the clipped category list keyboard scrollable and named */
    var scroller = dlg.querySelector('.body');
    if (scroller && scroller.scrollHeight > scroller.clientHeight + 4) {
      scroller.setAttribute('tabindex', '0');
      scroller.setAttribute('role', 'group');
      if (!scroller.getAttribute('aria-label')) {
        scroller.setAttribute('aria-label', 'Consent categories');
      }
    }

    /* A11Y-005 — a real close control, so a keyboard user can back out
       without being forced to commit a consent decision */
    if (!dlg.querySelector('.a11y-consent-close')) {
      var header = dlg.querySelector('.header') || dlg.firstElementChild;
      if (header) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'a11y-consent-close';
        btn.setAttribute('aria-label', 'Close consent preferences');
        btn.textContent = '×';
        btn.style.cssText = 'background:none;border:0;color:inherit;font-size:22px;' +
                            'line-height:1;cursor:pointer;margin-inline-start:auto;padding:4px 8px;' +
                            'min-width:44px;min-height:44px;';
        btn.addEventListener('click', closePreferences);
        header.appendChild(btn);
      }
    }
  }

  /* ------------------------- A11Y-001 / A11Y-002 — make the modal a real modal */

  function applyInert(host) {
    releaseInert();
    Array.prototype.forEach.call(document.body.children, function (el) {
      if (el === host || el.hasAttribute('inert')) return;
      el.setAttribute('inert', '');
      inerted.push(el);
    });
  }

  function releaseInert() {
    inerted.forEach(function (el) { el.removeAttribute('inert'); });
    inerted = [];
  }

  function trapFocus(dlg) {
    releaseTrap();
    trapHandler = function (ev) {
      if (ev.key === 'Escape') {           // A11Y-005
        ev.preventDefault();
        closePreferences();
        return;
      }
      if (ev.key !== 'Tab') return;
      var items = focusables(dlg);
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1], cur = deepActive();
      if (ev.shiftKey && (cur === first || !dlg.contains(cur))) {
        ev.preventDefault(); last.focus();
      } else if (!ev.shiftKey && (cur === last || !dlg.contains(cur))) {
        ev.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', trapHandler, true);
  }

  function releaseTrap() {
    if (trapHandler) {
      document.removeEventListener('keydown', trapHandler, true);
      trapHandler = null;
    }
  }

  function closePreferences() {
    var sr = shadow();
    if (!sr) return;
    releaseTrap();
    releaseInert();
    // Re-run the CMP's own "back to notice" path if it exposes one, otherwise
    // just dismiss the preferences view without writing a consent decision.
    var dlg = sr.querySelector('dialog');
    if (dlg) dlg.removeAttribute('open');
    announce(sr, 'Consent preferences closed. No changes were saved.');
    if (lastTrigger && document.contains(lastTrigger.getRootNode().host || lastTrigger)) {
      try { lastTrigger.focus(); } catch (e) {}
    }
  }

  /* ----------------------------------------------------------- render hooks */

  function shadow() {
    var host = document.querySelector(HOST_TAG);
    return host && host.shadowRoot ? host.shadowRoot : null;
  }

  function onRender() {
    var host = document.querySelector(HOST_TAG);
    var sr = shadow();
    if (!sr) return;

    injectCss(sr);

    var dlg = sr.querySelector('dialog[open]');
    var widget = sr.querySelector('button.widget');

    if (!dlg) {
      // Dialog gone: consent was saved, or preferences were closed.
      releaseTrap();
      releaseInert();
      if (widget && !widget.dataset.a11yDone) {
        widget.dataset.a11yDone = '1';
        announce(sr, 'Your cookie preferences have been saved.');   // A11Y-006
      }
      return;
    }

    patchDialog(sr, dlg);

    if (dlg.classList.contains('preferences')) {
      // A11Y-001 / A11Y-002 — trap focus and take the page out of the a11y tree
      applyInert(host);
      trapFocus(dlg);
      if (!dlg.dataset.a11yFocused) {
        dlg.dataset.a11yFocused = '1';
        var first = focusables(dlg)[0];
        if (first) first.focus();
        announce(sr, 'Consent preferences dialog opened.');
      }
    } else {
      // Notice: not trapped (it is a corner notice, the page stays usable),
      // but it must be reachable and announced.
      releaseTrap();
      releaseInert();
      if (!noticeAnnounced) {
        noticeAnnounced = true;
        lastTrigger = deepActive();
        var h = dlg.querySelector('h2, .title');
        if (h) {
          h.setAttribute('tabindex', '-1');
          // A11Y-003 — otherwise this is the 57th tab stop on the page
          window.setTimeout(function () { h.focus({ preventScroll: true }); }, 120);
        }
        announce(sr, 'A cookie consent choice is required. Use the buttons in this notice to accept, decline, or open preferences.');
      }
      // remember the Preferences button so focus can come back to it
      var pref = Array.prototype.filter.call(dlg.querySelectorAll('button'), function (b) {
        return /preferences|customi[sz]e|manage/i.test(b.textContent || '');
      })[0];
      if (pref) {
        pref.addEventListener('click', function () { lastTrigger = pref; }, { once: true });
      }
    }
  }

  /* -------------------------------------------------------------- bootstrap */

  function boot() {
    var host = document.querySelector(HOST_TAG);
    if (!host || !host.shadowRoot) return false;
    onRender();
    new MutationObserver(function () { window.setTimeout(onRender, 0); })
      .observe(host.shadowRoot, { childList: true, subtree: true, attributes: true,
                                  attributeFilter: ['open', 'class', 'aria-checked'] });
    return true;
  }

  $(function () {
    if (boot()) return;
    // The CMP is injected asynchronously; watch <body> until its host appears.
    var bodyObserver = new MutationObserver(function () {
      if (boot()) bodyObserver.disconnect();
    });
    bodyObserver.observe(document.body, { childList: true });
    window.setTimeout(function () { bodyObserver.disconnect(); }, 30000);
  });

})(jQuery);